import {
  Document,
  Packer,
  Paragraph,
  TextRun,
  AlignmentType,
  LevelFormat,
  ImageRun,
  Header,
  Footer,
} from 'docx';
import { saveAs } from 'file-saver';
import {
  cleanText,
  preprocessMarkdown,
  normalizeMarkdownText,
} from './markdownEngine';
import {
  sendDownloadLog,
  upsertRequestDocument,
  sendWordRequest,
} from '../services/firestore';
import { uploadFile, downloadFileFromStorage } from '../services/storage';
import { getClientId } from '../config/domain';
import {
  collection,
  addDoc,
  serverTimestamp,
  doc,
  onSnapshot,
} from 'firebase/firestore';
import { firestore } from '../services/firebase';
import headerImage from '../assets/images/header.png';

// Function to create word-request document and wait for completion
const createWordRequestAndWait = (requestId, fileUrl) => {
  return new Promise(async (resolve, reject) => {
    try {
      // Create word-request document
      const wordRequestRef = await addDoc(
        collection(firestore, 'word-requests'),
        {
          requestId,
          fileUrl,
          status: 'CREATED',
          createdAt: serverTimestamp(),
        },
      );

      console.log('Word request created:', wordRequestRef.id);

      // Listen for status changes
      const unsubscribe = onSnapshot(
        doc(firestore, 'word-requests', wordRequestRef.id),
        (docSnapshot) => {
          if (docSnapshot.exists()) {
            const data = docSnapshot.data();
            console.log('Word request status:', data.status);

            if (data.status === 'DONE') {
              unsubscribe(); // Stop listening
              resolve(data);
            } else if (data.status === 'ERROR') {
              unsubscribe();
              reject(new Error('Word processing failed'));
            }
          }
        },
        (error) => {
          console.error('Error listening to word request:', error);
          unsubscribe();
          reject(error);
        },
      );

      // Set a timeout to avoid infinite waiting (30 minutes)
      setTimeout(() => {
        unsubscribe();
        reject(new Error('Word processing timeout'));
      }, 30 * 60 * 1000);
    } catch (error) {
      console.error('Error creating word request:', error);
      reject(error);
    }
  });
};

// Function to download file from Firebase Storage (avoids CORS issues)
const downloadProcessedFile = async (storagePath, filename) => {
  try {
    const blob = await downloadFileFromStorage(storagePath);
    saveAs(blob, filename);
    return true;
  } catch (error) {
    console.error('Error downloading processed file:', error);
    throw error;
  }
};

// Helper function to process italic text - DOCX specific
const processItalicInTextRuns = (text, isBold = false) => {
  const runs = [];
  // Trim leading whitespace to prevent unwanted horizontal spacing
  let currentText = text.trimStart();

  while (currentText.includes('_')) {
    const startIndex = currentText.indexOf('_');
    if (startIndex > 0) {
      runs.push(
        new TextRun({
          text: currentText.substring(0, startIndex),
          bold: isBold,
          color: '000000',
          size: 22,
        }),
      );
    }

    const endIndex = currentText.indexOf('_', startIndex + 1);
    if (endIndex === -1) {
      runs.push(
        new TextRun({
          text: currentText.substring(startIndex),
          bold: isBold,
          color: '000000',
          size: 22,
        }),
      );
      currentText = '';
      break;
    }

    runs.push(
      new TextRun({
        text: currentText.substring(startIndex + 1, endIndex),
        italics: true,
        bold: isBold,
        color: '000000',
        size: 22,
      }),
    );

    currentText = currentText.substring(endIndex + 1);
  }

  if (currentText) {
    runs.push(
      new TextRun({
        text: currentText,
        bold: isBold,
        color: '000000',
        size: 22,
      }),
    );
  }

  return runs;
};

// Helper function to process formatted text (bold/italic) - DOCX specific
const processFormattedTextRuns = (text) => {
  const runs = [];
  // Trim leading whitespace to prevent unwanted horizontal spacing
  let currentText = text.trimStart();

  // First, standardize bold formatting - convert single * to **
  // But preserve existing ** formatting
  let processedText = currentText;
  // Replace ** temporarily to avoid double processing
  processedText = processedText.replace(/\*\*/g, '§BOLD§');
  // Convert single * to **
  processedText = processedText.replace(/\*([^§\n*]+?)\*/g, '**$1**');
  // Restore ** markers
  processedText = processedText.replace(/§BOLD§/g, '**');
  currentText = processedText;

  // Process bold text first (**)
  while (currentText.includes('**')) {
    const startIndex = currentText.indexOf('**');
    if (startIndex > 0) {
      // Add text before bold as regular text
      const beforeText = currentText.substring(0, startIndex);
      if (beforeText.includes('_')) {
        runs.push(...processItalicInTextRuns(beforeText, false));
      } else {
        runs.push(
          new TextRun({
            text: beforeText,
            color: '000000',
            size: 22,
          }),
        );
      }
    }

    const endIndex = currentText.indexOf('**', startIndex + 2);
    if (endIndex === -1) {
      // No closing **, treat as normal text
      const remainingText = currentText.substring(startIndex);
      if (remainingText.includes('_')) {
        runs.push(...processItalicInTextRuns(remainingText, false));
      } else {
        runs.push(
          new TextRun({
            text: remainingText,
            color: '000000',
            size: 22,
          }),
        );
      }
      currentText = '';
      break;
    }

    const boldText = currentText.substring(startIndex + 2, endIndex);

    // Check if bold text also has italic markers
    if (boldText.includes('_')) {
      runs.push(...processItalicInTextRuns(boldText, true));
    } else {
      runs.push(
        new TextRun({
          text: boldText,
          bold: true,
          color: '000000',
          size: 22,
        }),
      );
    }

    currentText = currentText.substring(endIndex + 2);
  }

  if (currentText) {
    // Process italic in remaining text
    if (currentText.includes('_')) {
      runs.push(...processItalicInTextRuns(currentText, false));
    } else {
      runs.push(
        new TextRun({
          text: currentText,
          color: '000000',
          size: 22,
        }),
      );
    }
  }

  return runs;
};

// Helper function to convert image to base64
const imageToBase64 = async (imagePath) => {
  try {
    const response = await fetch(imagePath);
    if (!response.ok) {
      console.error(
        'Failed to fetch image:',
        response.status,
        response.statusText,
      );
      return null;
    }

    const blob = await response.blob();

    // Validate that it's actually an image
    if (!blob.type.startsWith('image/')) {
      console.error('Fetched content is not an image:', blob.type);
      return null;
    }

    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const result = reader.result;
        // Validate base64 result
        if (result && result.includes(',')) {
          resolve(result);
        } else {
          console.error('Invalid base64 result');
          resolve(null);
        }
      };
      reader.onerror = (error) => {
        console.error('FileReader error:', error);
        reject(error);
      };
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    console.error('Error converting image to base64:', error);
    return null;
  }
};

// DOCX generation main function
export const generateDOCX = async (
  petitionContent,
  hasPlaceholdersFunc,
  setModalConfig,
  setShowModal,
) => {
  // Check for placeholders first
  const placeholderCheck = hasPlaceholdersFunc();
  if (placeholderCheck.hasPlaceholders) {
    const missingValues = placeholderCheck.placeholders.join(', ');
    setModalConfig({
      type: 'word',
      message: `Belgede şu eksik değerler bulunuyor: ${missingValues}. Yine de indirmek ister misiniz?`,
      buttons: [
        {
          text: 'Evet',
          type: 'primary',
          onClick: () => {
            setShowModal(false);
            // This should be handled by the caller
            // proceedWithDOCXGeneration();
          },
        },
        {
          text: 'İptal Et',
          type: 'secondary',
          onClick: () => setShowModal(false),
        },
      ],
    });
    setShowModal(true);
    return { shouldProceed: false };
  }

  // If no placeholders, proceed directly
  return { shouldProceed: true };
};

// Proceed with DOCX generation
export const proceedWithDOCXGeneration = async (
  petitionContent,
  documentData = null,
  selectedTemplateId = null,
  dictionary = null,
  userEmail = null,
  requestId = null,
  documentOrder = null,
) => {
  // Set downloading status indicator
  const downloadBtn = document.getElementById('download-word-btn');
  downloadBtn.disabled = true;
  downloadBtn.textContent = 'İşleniyor...';
  try {
    // Get the formatted content that has proper markdown line breaks
    let markdownText = normalizeMarkdownText(petitionContent);

    const cleanedMarkdownText = cleanText(markdownText);
    const preprocessedText = preprocessMarkdown(cleanedMarkdownText);
    const lines = preprocessedText.split('\n');

    // Document children array for docx
    const docChildren = [];
    let currentNumberedList = [];
    let currentBulletList = [];
    let justProcessedHeader = false; // Track if we just processed a header
    let previousLineWasEmpty = false; // Track if previous line was empty for paragraph spacing

    // Helper function to add numbered list
    const addNumberedList = () => {
      if (currentNumberedList.length > 0) {
        docChildren.push(...currentNumberedList);
        currentNumberedList = [];
      }
    };

    // Helper function to add bullet list
    const addBulletList = () => {
      if (currentBulletList.length > 0) {
        docChildren.push(...currentBulletList);
        currentBulletList = [];
      }
    };

    // Process each line
    lines.forEach((line) => {
      const hasTabIndent = line.includes('[[TAB_INDENT]]');

      // Remove the markers for actual content processing
      const lineWithBreaks = line
        .replace(/\[\[DOUBLE_NEWLINE\]\]/g, '')
        .replace(/\[\[SINGLE_LINEBREAK\]\]/g, '')
        .replace(/\[\[TAB_INDENT\]\]/g, '');

      // Clean the line
      const cleanedLine = cleanText(lineWithBreaks);

      // Check if line starts with >> for right alignment
      const isRightAligned = cleanedLine.startsWith('>>');

      // Handle empty lines
      if (!cleanedLine.trim()) {
        // Mark that this line was empty for consistent paragraph spacing
        previousLineWasEmpty = true;
        justProcessedHeader = false; // Reset flag for empty lines
        return;
      }

      // Handle markdown headings first
      if (lineWithBreaks.startsWith('# ')) {
        addNumberedList();
        addBulletList();
        // Trim any leading whitespace from heading text
        const headingText = lineWithBreaks.substring(2).trimStart();

        docChildren.push(
          new Paragraph({
            children: [
              new TextRun({
                text: headingText,
                size: 32, // Larger font for h1
                bold: true,
                color: '000000',
              }),
            ],
            alignment: AlignmentType.CENTER,
            spacing: { before: 400, after: 200 }, // Increased spacing for h1
          }),
        );
        previousLineWasEmpty = false; // Reset flag
        justProcessedHeader = true; // Set flag after processing header
      } else if (lineWithBreaks.startsWith('## ')) {
        addNumberedList();
        addBulletList();
        // Trim any leading whitespace from heading text
        const headingText = lineWithBreaks.substring(3).trimStart();

        docChildren.push(
          new Paragraph({
            children: [
              new TextRun({
                text: headingText,
                size: 28, // Medium font for h2
                bold: true,
                color: '000000',
              }),
            ],
            alignment: AlignmentType.JUSTIFIED,
            spacing: { before: 300, after: 150 }, // Increased spacing for h2
          }),
        );
        previousLineWasEmpty = false; // Reset flag
        justProcessedHeader = true; // Set flag after processing header
      } else if (lineWithBreaks.startsWith('### ')) {
        addNumberedList();
        addBulletList();
        // Trim any leading whitespace from heading text
        const headingText = lineWithBreaks.substring(4).trimStart();

        docChildren.push(
          new Paragraph({
            children: [
              new TextRun({
                text: headingText,
                size: 24, // Smaller font for h3
                bold: true,
                color: '000000',
              }),
            ],
            alignment: AlignmentType.JUSTIFIED,
            spacing: { before: 200, after: 100 }, // Increased spacing for h3
          }),
        );
        previousLineWasEmpty = false; // Reset flag
        justProcessedHeader = true; // Set flag after processing header
      }
      // Handle horizontal rule (---, *** or ___)
      else if (/^[-*_]{3,}$/.test(lineWithBreaks.trim())) {
        addNumberedList();
        addBulletList();
        docChildren.push(
          new Paragraph({
            children: [new TextRun({ text: '', size: 22 })],
            alignment: AlignmentType.CENTER,
            spacing: { before: 100, after: 100 },
            border: {
              bottom: {
                color: '000000',
                space: 1,
                style: 'single',
                size: 6,
              },
            },
          }),
        );
        previousLineWasEmpty = false; // Reset flag
        justProcessedHeader = false; // Reset flag for horizontal rules
      }
      // Handle numbered lists
      else if (/^\d+\.\s+/.test(lineWithBreaks)) {
        addBulletList();
        const parts = lineWithBreaks.match(/^(\d+)\.\s*(.*)/);
        // Trim any leading whitespace from list item text
        const textPart = (parts[2] || lineWithBreaks).trimStart();

        const textRuns = processFormattedTextRuns(textPart);

        currentNumberedList.push(
          new Paragraph({
            children: textRuns,
            numbering: {
              reference: 'arbitration-numbering',
              level: 0,
            },
            alignment: AlignmentType.JUSTIFIED,
            spacing: { before: 0, after: 20 },
          }),
        );
        previousLineWasEmpty = false; // Reset flag
        justProcessedHeader = false; // Reset flag for numbered lists
      }
      // Handle bullet lists
      else if (/^[-*•]\s+/.test(lineWithBreaks)) {
        addNumberedList();
        // Trim any leading whitespace from bullet list text
        let listText = lineWithBreaks.replace(/^[-*•]\s*/, '').trimStart();
        const textRuns = processFormattedTextRuns(listText);

        currentBulletList.push(
          new Paragraph({
            children: textRuns,
            bullet: { level: 0 },
            alignment: AlignmentType.JUSTIFIED,
            spacing: { before: 0, after: 20 },
          }),
        );
        previousLineWasEmpty = false; // Reset flag
        justProcessedHeader = false; // Reset flag for bullet lists
      }
      // Handle right-aligned text (starting with >>)
      else if (isRightAligned) {
        addNumberedList();
        addBulletList();

        // Remove the >> marker and process the text
        const rightAlignedText = cleanedLine.substring(2);
        const textRuns = processFormattedTextRuns(rightAlignedText);

        // Determine spacing based on whether we just processed a header or if previous line was empty
        const paragraphSpacing = justProcessedHeader
          ? { before: 0, after: 20 } // No before spacing after headers
          : previousLineWasEmpty
          ? { before: 200, after: 20 } // Extra spacing if previous line was empty (paragraph break)
          : { before: 0, after: 20 }; // Normal spacing for continuation text

        docChildren.push(
          new Paragraph({
            children: textRuns,
            alignment: AlignmentType.RIGHT,
            spacing: paragraphSpacing,
          }),
        );

        previousLineWasEmpty = false; // Reset flag
        justProcessedHeader = false; // Reset flag for right-aligned text
      }
      // Handle regular paragraphs
      else {
        addNumberedList();
        addBulletList();

        // Determine spacing based on whether we just processed a header or if previous line was empty
        const paragraphSpacing = justProcessedHeader
          ? { before: 0, after: 20 } // No before spacing after headers
          : previousLineWasEmpty
          ? { before: 200, after: 20 } // Extra spacing if previous line was empty (paragraph break)
          : { before: 0, after: 20 }; // Normal spacing for continuation text

        // Determine indentation - add first line indent for tab-initiated paragraphs
        const paragraphIndent = hasTabIndent
          ? { firstLine: 360 } // 360 twips = 0.25 inch first line indent (reduced from 720)
          : undefined;

        // Handle label lines (e.g., "_Dosya No_:")
        const isLabelLine = /^_[^_]+_:/.test(lineWithBreaks);

        if (isLabelLine) {
          const labelMatch = lineWithBreaks.match(/^_([^_]+)_:(.*)/);
          if (labelMatch) {
            const labelText = labelMatch[1];
            // Trim any leading whitespace from value text
            const valueText = labelMatch[2].trimStart();

            docChildren.push(
              new Paragraph({
                children: [
                  new TextRun({
                    text: labelText + ': ',
                    bold: true,
                    italics: true,
                    color: '000000',
                    size: 22,
                  }),
                  new TextRun({
                    text: valueText,
                    color: '000000',
                    size: 22,
                  }),
                ],
                alignment: AlignmentType.JUSTIFIED,
                spacing: paragraphSpacing,
                indent: paragraphIndent,
              }),
            );
          } else {
            const textRuns = processFormattedTextRuns(lineWithBreaks);
            docChildren.push(
              new Paragraph({
                children: textRuns,
                alignment: AlignmentType.JUSTIFIED,
                spacing: paragraphSpacing,
                indent: paragraphIndent,
              }),
            );
          }
        } else {
          const textRuns = processFormattedTextRuns(lineWithBreaks);
          docChildren.push(
            new Paragraph({
              children: textRuns,
              alignment: AlignmentType.JUSTIFIED,
              spacing: paragraphSpacing,
              indent: paragraphIndent,
            }),
          );
        }

        previousLineWasEmpty = false; // Reset flag after processing paragraph
      }

      // Reset the flag after processing first paragraph after header
      justProcessedHeader = false;
    });

    // Add any remaining lists
    addNumberedList();
    addBulletList();

    // Generate filename
    let filename = 'tahkim-davasi';
    if (documentData && selectedTemplateId) {
      // Format: {arbitrationNumber} {applicantName} {selectedTemplateId} CD
      const arbitrationNumber = documentData.arbitrationNumber || '';
      const applicantName = documentData.applicantName || '';

      // Use dictionary value for templateId if available, otherwise use templateId
      const templateDisplayName =
        (dictionary && dictionary[selectedTemplateId]) || selectedTemplateId;

      // Clean the values to make them safe for filenames (extra strict for government systems)
      const cleanArbitrationNumber = arbitrationNumber
        .replace(/[<>:"/\\|?*çğıöşüÇĞIİÖŞÜ]/g, '')
        .replace(/\s+/g, '_')
        .trim();
      const cleanApplicantName = applicantName
        .replace(/[<>:"/\\|?*çğıöşüÇĞIİÖŞÜ]/g, '')
        .replace(/\s+/g, '_')
        .trim();
      const cleanTemplateDisplayName = templateDisplayName
        .replace(/[<>:"/\\|?*çğıöşüÇĞIİÖŞÜ]/g, '')
        .replace(/\s+/g, '_')
        .trim();

      if (
        cleanArbitrationNumber ||
        cleanApplicantName ||
        cleanTemplateDisplayName
      ) {
        const parts = [
          cleanArbitrationNumber,
          cleanApplicantName,
          cleanTemplateDisplayName,
          'CD',
        ].filter((part) => part);
        filename = parts.join('_'); // Use underscore instead of space for better government system compatibility
      }
    }
    filename += '.docx';

    // Header: image for dogandemir client, text for others (e.g. hergulkir)
    const clientId = getClientId();
    const headerImageBase64 =
      clientId === 'dogandemir' ? await imageToBase64(headerImage) : null;

    // Create the document
    const doc = new Document({
      numbering: {
        config: [
          {
            reference: 'arbitration-numbering',
            levels: [
              {
                level: 0,
                format: LevelFormat.DECIMAL,
                text: '%1.',
                alignment: AlignmentType.LEFT,
                start: 1,
                style: {
                  paragraph: {
                    indent: { left: 360, hanging: 280 },
                    spacing: { before: 0, after: 40 },
                  },
                  run: {
                    size: 22,
                    color: '000000',
                    font: 'Calibri',
                  },
                },
              },
            ],
          },
        ],
      },
      // Add metadata including requestId
      coreProperties: {
        subject: `RequestId: ${requestId || 'unknown'}`,
        title: filename.replace('.docx', ''),
        creator: 'Arbitration System',
        description: 'Generated arbitration document',
      },
      customProperties: [
        {
          name: 'requestId',
          value: requestId || 'unknown',
        },
      ],
      sections: [
        {
          properties: {
            page: {
              margin: {
                top: 800,
                right: 1134,
                bottom: 567, // Half of 1134
                left: 1134,
                header: 200,
                footer: 354, // Half of 708
              },
            },
          },
          headers: headerImageBase64
            ? {
                default: new Header({
                  children: [
                    new Paragraph({
                      children: [
                        new ImageRun({
                          data: headerImageBase64.split(',')[1],
                          transformation: { width: 96, height: 32 },
                        }),
                      ],
                      alignment: AlignmentType.CENTER,
                      spacing: { after: 600 },
                    }),
                  ],
                }),
              }
            : {
                default: new Header({
                  children: [
                    new Paragraph({
                      children: [
                        new TextRun({
                          text: 'HERGÜL&KIR',
                          bold: true,
                          size: 22,
                          color: '000000',
                        }),
                      ],
                      alignment: AlignmentType.CENTER,
                      spacing: { after: 100 },
                    }),
                    new Paragraph({
                      children: [
                        new TextRun({
                          text: 'Hukuk ve Danışmanlık',
                          bold: false,
                          size: 22,
                          color: '000000',
                        }),
                      ],
                      alignment: AlignmentType.CENTER,
                      spacing: { after: 600 },
                    }),
                  ],
                }),
              },
          footers: {
            default: new Footer({
              children:
                clientId === 'dogandemir'
                  ? [
                      new Paragraph({
                        children: [
                          new TextRun({
                            text: 'Kaptanpaşa Mah. Halit Ziya Türkkan Sk. No:19 Famas Plaza C blok Kat 9 Şişli/İSTANBUL',
                            bold: true,
                            size: 18,
                            color: '000000',
                          }),
                        ],
                        alignment: AlignmentType.CENTER,
                        spacing: { before: 200, after: 100 },
                      }),
                      new Paragraph({
                        children: [
                          new TextRun({
                            text: 'tahkim@dogandemir.av.tr      +90 212 416 28 03',
                            bold: true,
                            size: 18,
                            color: '000000',
                          }),
                        ],
                        alignment: AlignmentType.CENTER,
                        spacing: { after: 50 },
                      }),
                    ]
                  : [
                      new Paragraph({
                        children: [
                          new TextRun({
                            text: 'Adres: Kuştepe Mah. leylak-1 Sk. No:3 K:1 D:7 Murat İş Merkezi Şişli/İSTANBUL',
                            bold: false,
                            size: 18,
                            color: '000000',
                          }),
                        ],
                        alignment: AlignmentType.CENTER,
                        spacing: { before: 200, after: 100 },
                      }),
                      new Paragraph({
                        children: [
                          new TextRun({
                            text: 'www.hergulkirhukukvedanismanlik.com',
                            bold: false,
                            size: 18,
                            color: '000000',
                          }),
                        ],
                        alignment: AlignmentType.CENTER,
                        spacing: { after: 50 },
                      }),
                      new Paragraph({
                        children: [
                          new TextRun({
                            text: 'M. 0554 279 37 41',
                            bold: false,
                            size: 18,
                            color: '000000',
                          }),
                        ],
                        alignment: AlignmentType.CENTER,
                        spacing: { after: 50 },
                      }),
                      new Paragraph({
                        children: [
                          new TextRun({
                            text: 'M. 0536 694 21 15',
                            bold: false,
                            size: 18,
                            color: '000000',
                          }),
                        ],
                        alignment: AlignmentType.CENTER,
                        spacing: { after: 50 },
                      }),
                    ],
            }),
          },
          children: docChildren,
        },
      ],
    });

    // Generate and save the document
    const blob = await Packer.toBlob(doc);

    // Create a new blob with explicit MIME type for better compatibility with government systems
    const docxBlob = new Blob([blob], {
      type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    });

    // Upload the document to Firebase Storage (client-scoped path)
    const uploadPath = `outputs/${clientId}/${requestId}.docx`;
    const fileUrl = await uploadFile(docxBlob, uploadPath);

    // Update button status to show processing
    const downloadBtn = document.getElementById('download-word-btn');
    if (downloadBtn) {
      downloadBtn.textContent = 'Belge İşleniyor...';
    }

    // Wait for word-request completion
    const processedData = await createWordRequestAndWait(requestId, fileUrl);

    // Update button status to show downloading
    if (downloadBtn) {
      downloadBtn.textContent = 'İndiriliyor...';
    }

    // Extract storage path from the returned URL
    const finalFileUrl = processedData.url;
    const storagePath = finalFileUrl.replace(
      'https://storage.googleapis.com/arbitration-documents/',
      '',
    );

    console.log('Final file URL:', finalFileUrl);
    console.log('Extracted storage path:', storagePath);

    // Download the processed file from Firebase Storage (avoids CORS issues)
    await downloadProcessedFile(storagePath, filename);

    // Send download log
    await sendDownloadLog(
      userEmail,
      selectedTemplateId,
      'docx',
      filename,
      requestId,
    );

    // Save document data to Firestore before download
    try {
      await upsertRequestDocument(
        requestId,
        documentData,
        documentOrder,
        selectedTemplateId,
        userEmail,
      );

      console.log('Document data saved to Firestore successfully');
    } catch (saveError) {
      console.error('Error saving document data to Firestore:', saveError);
      // Continue with download even if save fails
    }

    return { success: true };
  } catch (error) {
    console.error('DOCX oluşturma hatası:', error);
    alert(
      'Word dökümanı oluşturulamadı. Lütfen tekrar deneyin: ' + error.message,
    );
    return { success: false, error: error.message };
  } finally {
    // Reset button state
    const downloadBtn = document.getElementById('download-word-btn');
    if (downloadBtn) {
      downloadBtn.textContent = 'Word İndir';
      downloadBtn.disabled = false;
    }
  }
};
